import React from 'react'
import styled from 'styled-components'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

interface TabItem {
  key: string
  label: string
  component: React.ReactNode
  show?: boolean
  name?: string
}

interface CustomTabsProps {
  items: TabItem[]
  defaultActiveKey?: string
  rightSlot?: React.ReactNode
}

const TabContainer = styled.div`
  border-radius: 4px;
  height: calc(100vh - 56px);
  overflow-y: scroll;
`

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
`

const TabItemsWrapper = styled.div`
  display: flex;
`

const TabItemStyled = styled.div<{ isActive: boolean; isFirst: boolean }>`
  width: 116px;
  height: 34px;
  background: ${props =>
    props.isActive
      ? 'linear-gradient(270deg, #bfbdea 0%, #eaeaff 100%)'
      : 'linear-gradient( 270deg, #F0F1F1 0%, #E6E6E6 100%)'};
  /* 缩放后的clip-path */
  clip-path: ${props =>
    props.isFirst
      ? 'path("M0,34 L0,0 L90,0 Q101,3.6 105,17 Q108,27.2 116,34 Z")'
      : 'path("M26.2,34 Q19,30.2 14.5,17 Q11,4.1 0,0 L90,0 Q101,3.1 105,18 Q108,27.2 116,34 Z")'};
  color: ${props => (props.isActive ? '#4238C9' : '#333333')};
  line-height: 34px;
  text-align: center;
  margin-left: ${props => (props.isFirst ? '0' : '-26px')};

  &:hover {
    color: #4238c9;
    cursor: pointer;
  }

  &::before {
    content: ${props => (props.isFirst ? 'none' : "''")};
    width: 17px;
    height: 17px;
    position: absolute;
    background: radial-gradient(circle at 0 100%, transparent 17px, #e6e6e6 17px);
    top: 0;
    left: -17px;
  }

  &::after {
    content: '';
    width: 17px;
    height: 17px;
    position: absolute;
    background: ${props =>
      props.isActive
        ? 'radial-gradient(circle at 100% 0, transparent 17px, #bfbdea 17px)'
        : 'radial-gradient(circle at 100% 0, transparent 17px, #F0F1F1 17px)'};
    bottom: 0;
    right: -17px;
  }
`

const TabContent = styled.div`
  height: calc(100% - 45px);
`

const RightSlot = styled.div`
  display: flex;
  align-items: center;
`

export const CustomTabs: React.FC<CustomTabsProps> = ({ items, rightSlot }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const activeTab = searchParams.get('tab')
  const name = searchParams.get('name')

  // 获取当前应该显示的 tab
  const getActiveTab = () => {
    // 如果 URL 中有 name 参数，找到对应的 tab
    if (name) {
      const namedTab = items.find(item => item.name === name)
      if (namedTab) return namedTab.key
    }
    // 否则使用 URL 中的 tab 参数
    return activeTab || items[0]?.key
  }

  const currentActiveTab = getActiveTab()

  const handleTabChange = (key: string) => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('tab', key)
    // 如果切换 tab，清除 name 参数
    newSearchParams.delete('name')
    navigate(`${pathname}?${newSearchParams.toString()}`)
  }

  // 获取当前应该显示的内容
  const getActiveContent = () => {
    // 如果 URL 中有 name 参数，找到对应的内容
    if (name) {
      const namedItem = items.find(item => item.name === name)
      if (namedItem) return namedItem.component
    }
    // 否则使用当前激活的 tab 的内容
    return items.find(item => item.key === currentActiveTab)?.component
  }

  return (
    <TabContainer>
      <TabHeader>
        <TabItemsWrapper>
          {items
            .filter(item => item.show !== false)
            .map((item, index) => (
              <TabItemStyled
                key={item.key}
                isActive={item.key === currentActiveTab}
                isFirst={index === 0}
                onClick={() => handleTabChange(item.key)}
              >
                {item.label}
              </TabItemStyled>
            ))}
        </TabItemsWrapper>
        {rightSlot && <RightSlot>{rightSlot}</RightSlot>}
      </TabHeader>
      <TabContent>{getActiveContent()}</TabContent>
    </TabContainer>
  )
}
